// Type imports
import type React from 'react'
import type { NextPage, GetServerSideProps } from 'next'
import type { UserConfig } from 'next-i18next'
import type { SerializedStyles } from '@emotion/react'
import type { ActionTypes } from '../types'
import type { RequiredNs as ComponentNs } from '../components/localized/types'

// External imports
import Image from '../components/NextImage'
import { useRouter } from 'next/router'
import { NextSeo } from 'next-seo'
import { serverSideTranslations } from 'next-i18next/serverSideTranslations'
import { Trans, useTranslation } from 'react-i18next'

import { useState } from 'react'

import styled from '@emotion/styled'
import { css } from '@emotion/react'

// Misc
import useScrollPassed from '../hooks/use-scrolled-passed'

import Header from '../components/localized/Header'
import Footer from '../components/localized/Footer'

import { Title as HeroTitle, Description as HeroDescription } from '../components/Hero'
import { Section } from '../components/Section'
import { CenteredSection, Title } from '../components/CenteredSection'

import PrimaryAction, { Props as PrimaryActionProps } from '../components/PrimaryAction'
import BottomCta from '../components/BottomCta'
import FeatureGrid from '../components/FeatureGrid'
import Highlight from '../components/Highlight'

import CheckIcon from '../../public/assets/icons/check.svg'
import MinusIcon from '../../public/assets/icons/minus.svg'

import { API_URL, defaultPageSpecificSEO, MOBILE_WIDTH_PX, pageSpecificCtaLink } from '../constants'
import { COMMON_DEFAULT_CSS, DEFAULT, TITLES } from '../font'

/**
 * Constants
 */

interface Headers {
  [s: string]: string | string[] | undefined
}

const HEADERS = {
  'x-platform': 'web-app',
  'x-version': 'v4.0.3+5.g7475bca'
}

/**
 * Load pricings
 */

interface Pricings {
  billingCycle: string
  monthlyPrice: number
  months: number
  originalMonthlyPrice: number
  originalPrice: number
  price: number
}

interface PricingsResponse {
  currency: { code: string, symbol: string }
  discount: {} // TODO: not currently used, but should probably still type this
  group: {
    minimumQuantity: number
    pricings: Pricings[]
    trialDuration: number
  }
  individual: {
    pricings: Pricings[]
    trialDuration: number
  }
}

interface BillingCycle {
  monthly: number
  yearly: number
}

interface PricingInfo extends Record<'individual' | 'group', BillingCycle> {
  symbol: string
}

const pricingsListToMap = (pricings: Pricings[]): BillingCycle => (
  pricings.reduce<BillingCycle>((acc, { billingCycle, monthlyPrice }) => (
    { ...acc, [billingCycle]: monthlyPrice }
  ), { monthly: Number.POSITIVE_INFINITY, yearly: Number.POSITIVE_INFINITY })
)

const loadPrice = async (
  additionalHeaders: Headers = {}
): Promise<PricingInfo> => {
  const url = `${API_URL}/pricings`

  const response = await fetch(url, {
    method: 'GET',
    headers: {
      ...HEADERS,
      ...additionalHeaders
    }
  })

  const data = await response.json() as PricingsResponse

  return {
    symbol: data.currency.symbol,
    individual: pricingsListToMap(data.individual.pricings),
    group: pricingsListToMap(data.group.pricings)
  }
}

/**
 * Load features
 */

interface Feature {
  free: boolean
  group: boolean
  id: string
  pro: boolean
  text: string
  features: Array<{ id: string, desc?: string, image?: string, text: string }>
}

interface FeaturesResponse {
  features: Feature[]
}

const loadFeatures = async (additionalHeaders: Headers = {}): Promise<Feature[]> => {
  const url = `${API_URL}/features`

  const response = await fetch(url, {
    method: 'GET',
    headers: {
      ...HEADERS,
      ...additionalHeaders
    }
  })

  const data = await response.json() as FeaturesResponse

  return data.features
}

/**
 * Props used by page
 */

interface Props {
  readonly pricingInfo: PricingInfo
  readonly featuresInfo: Feature[]

  // next-i18next internal props
  _nextI18Next: {
    initialI18nStore: any
    userConfig: UserConfig | null
  }
}

/**
 * Load all the namespaces required for this page include all localized components
 * & fetch pricings & features
 */
type RequiredNs = 'pricing' | ComponentNs
const namespaces: Record<RequiredNs, true> = { common: true, pricing: true }
export const getServerSideProps: GetServerSideProps<Props> = async ({ req, locale }) => {
  const reqSpecificHeaders: Headers = {
    'x-language': locale,
    'x-forwarded-for': req.headers['x-forwarded-for']
  }
  const [ssrConfig, pricingInfo, featuresInfo] = await Promise.all([
    serverSideTranslations(locale ?? '', Object.keys(namespaces)),
    loadPrice(reqSpecificHeaders),
    loadFeatures(reqSpecificHeaders)
  ])

  return {
    props: {
      ...ssrConfig,
      pricingInfo,
      featuresInfo
    }
  }
}

/**
 * Styles
 */

const HeroWrapper = styled.div`
  max-width: 820px;
  margin: 0 auto;
`

// Controls

const Controls = styled.div`
  display: flex;
  padding: 6px;
  border: 1px solid ${(props): string => props.theme.outwrite.colors.gray[700]};
  border-radius: 48px;
  width: fit-content;
  margin: 20px auto;

  flex-wrap: wrap;
  align-content: center;
  align-items: center;
  justify-content: center;
`

const LabelText = styled.span`
  padding: 8px 28px;
  border-radius: 48px;
`

const RadioInput = styled.input`
  display: none;
  appearance: none;
  background-color: transparent;
  margin: 0;

  &:checked + ${LabelText} {
    background: ${(props): string => props.theme.outwrite.colors.yellow[500]};
  }
`

const Label = styled.label`
  flex: 1 1 0;
  cursor: pointer;
  margin: 8px 0; // Account for Label text padding
  margin-right: 8px;
  &:last-of-type {
    margin-right: 0;
  }
  text-align: center;
`

// Layout

const Table = styled.div`
  display: flex;
  text-align: left;
  flex-wrap: wrap;
`

const Column = styled.div`
  flex: 1 1 0;
  margin: 0 16px;
  @media screen and (max-width: ${MOBILE_WIDTH_PX}px) {
    flex-basis: 100%; // Become one column
  }
`

// Title

const ColumnTitle = styled.h2`
  ${TITLES[900]}
  margin: 64px 0 4px 0;
  display: flex;
  align-items: baseline;
  flex-wrap: wrap;
  @media screen and (max-width: ${MOBILE_WIDTH_PX}px) {
    ${TITLES[700]}
  }
`
const SymbolPrice = styled.span`
  ${TITLES[700]}
  @media screen and (max-width: ${MOBILE_WIDTH_PX}px) {
    ${TITLES[600]}
  }
`
const LengthText = styled.span`
  ${TITLES[600]}
  @media screen and (max-width: ${MOBILE_WIDTH_PX}px) {
    ${TITLES[500]}
  }
`

const ColumnSubtitle = styled.p`
  ${COMMON_DEFAULT_CSS}
  ${DEFAULT[600]}
  font-weight: 700;
  margin: 0 0 8px 0;
  @media screen and (max-width: ${MOBILE_WIDTH_PX}px) {
    ${DEFAULT[500]}
  }
`
const ColumnDescription = styled.p`
  margin: 0 0 32px 0;
`

// Category

const Category = styled.div`
  margin-bottom: 32px;
`
const CategoryTitle = styled.h3`
  ${COMMON_DEFAULT_CSS}
  ${DEFAULT[400]}
  font-weight: 700;
`
const FeatureText = styled.div`
  display: flex;
  align-items: center;
  position: relative;
  margin: 16px 0;
`
const CheckIconWrapper = styled.div`
  position: relative;
  display: flex;
  align-items: center;
  margin-right: 12px;
  background: ${(props): string => props.theme.outwrite.colors.yellow[500]};
  height: 28px;
  width: 28px;
  border-radius: 100%;
  padding: 6px;
`
const MinusIconWrapper = styled(CheckIconWrapper)`
  background: ${(props): string => props.theme.outwrite.colors.gray[400]};
`

const TooltipWrapper = styled.div`
  position: absolute;
  top: 100%;
  margin-top: 8px;
  z-index: 1;
  background: ${(props): string => props.theme.outwrite.colors.background.dark[400]};
  color: ${(props): string => props.theme.outwrite.colors.white};
  padding: 12px;
  border-radius: ${(props): number => props.theme.outwrite.borderRadii.medium}px;
  opacity: 0;
  transition: opacity 250ms;
  transition-delay: 250ms;
  visibility: hidden;
  overflow: visible;
  max-width: 350px;
`

interface FeatureNameProps { hasDesc: boolean }
const FeatureName = styled.p<FeatureNameProps>`
  margin: 0;
  ${(props): SerializedStyles | string => props.hasDesc
    ? css`
      text-decoration: underline dashed;
      text-underline-position: under;
      &:hover {
        + ${TooltipWrapper} {
          opacity: 0.95;
          visibility: visible;
        }
      }
    `
  : ''}
`

const ImageWrapper = styled.div`
  position: relative;
  margin-top: 8px;
`

// Action

const ActionWrapper = styled.div`
  margin: 32px 0;
`

// Constants

const PRIMARY_ACTION_PROPS: Partial<PrimaryActionProps> = {
  clickId: 'pricing-cta',
  size: 'medium',
  responsive: [],
  useShort: true
}

// Actual component

const Pricing: NextPage<Props> = (props) => {
  const router = useRouter()

  const { t } = useTranslation('pricing')
  const { t: tCommon } = useTranslation('common')

  const ctaAction: ActionTypes = { ...t('CTA_BUTTON'), ...pageSpecificCtaLink(router.pathname, router.locale) }
  const paymentsCtaAction: ActionTypes = { ...t('CTA_BUTTON'), ...pageSpecificCtaLink('payments', router.locale) }

  // Controls for the header to only go sticky after user passes the fold
  const { ref: heroRef, scrolledPassed } = useScrollPassed()

  const [plan, setPlan] = useState<'yearly' | 'monthly'>('yearly')

  return (
    <>
      <NextSeo
        {...defaultPageSpecificSEO(router.pathname, router.locale)}
        {...t('SEO')}
      />

      <Header t={tCommon} action={ctaAction} data-sticky={scrolledPassed} />
      <Section>
        <HeroWrapper ref={heroRef}>
          <HeroTitle>
            <Trans i18nKey='hero.TITLE' t={t} components={[<Highlight key={0} color='yellow' />]}/>
          </HeroTitle>
          <HeroDescription>{t('hero.DESCRIPTION')}</HeroDescription>
        </HeroWrapper>
        <Controls>
          <Label>
            <RadioInput type="radio" name="radio" checked={plan === 'monthly'} onChange={(): void => setPlan('monthly')} />
            <LabelText>{t('MONTHLY')}</LabelText>
          </Label>
          <Label>
            <RadioInput type="radio" name="radio" checked={plan === 'yearly'} onChange={(): void => setPlan('yearly')} />
            <LabelText>{t('YEARLY')}</LabelText>
          </Label>
        </Controls>

        <Table>
          <Column>
            <ColumnTitle>{t('free.TITLE')}</ColumnTitle>
            <ColumnSubtitle>{t('free.SUBTITLE')}</ColumnSubtitle>
            <ColumnDescription>{t('free.DESCRIPTION')}</ColumnDescription>
            <ActionWrapper>
              <PrimaryAction {...PRIMARY_ACTION_PROPS} {...ctaAction} />
            </ActionWrapper>
            <div>
              {props.featuresInfo.map(({ id, text, features, free }) => (
                <Category key={id}>
                  <CategoryTitle>{text}</CategoryTitle>
                  {features.map(({ id, text, desc, image }) => (
                    <FeatureText key={id}>
                      {free
                        ? <CheckIconWrapper><Image src={CheckIcon} alt={t('free.CHECK_ICON_ALT')} /></CheckIconWrapper>
                        : <MinusIconWrapper><Image src={MinusIcon} alt={t('free.MINUS_ICON_ALT')} /></MinusIconWrapper>
                      }
                      <FeatureName hasDesc={!!desc}>{text}</FeatureName>
                      {desc && (
                        <TooltipWrapper>
                          {desc}
                          {image && <ImageWrapper><Image src={image} alt='' height={150} width={250} layout='responsive' /></ImageWrapper>}
                        </TooltipWrapper>
                      )}
                    </FeatureText>
                  ))}
                </Category>
              ))}
            </div>
          </Column>
          <Column>
            <ColumnTitle>
              <Trans
                t={t}
                i18nKey='pro.TITLE'
                values={{
                  symbol: props.pricingInfo.symbol,
                  pricing: props.pricingInfo.individual[plan]
                }}
                components={[
                  <SymbolPrice key={0} />,
                  <span key={1} />,
                  <LengthText key={2} />
                ]}
              />
            </ColumnTitle>
            <ColumnSubtitle>{t('pro.SUBTITLE')}</ColumnSubtitle>
            <ColumnDescription>{t('pro.DESCRIPTION')}</ColumnDescription>
            <ActionWrapper>
              <PrimaryAction {...PRIMARY_ACTION_PROPS} {...paymentsCtaAction} />
            </ActionWrapper>
            <div>
              {props.featuresInfo.map(({ id, text, features, pro }) => (
                <Category key={id}>
                  <CategoryTitle>{text}</CategoryTitle>
                  {features.map(({ id, text, desc, image }) => (
                    <FeatureText key={id}>
                      {pro
                        ? <CheckIconWrapper><Image src={CheckIcon} alt={t('pro.CHECK_ICON_ALT')} /></CheckIconWrapper>
                        : <MinusIconWrapper><Image src={MinusIcon} alt={t('pro.MINUS_ICON_ALT')} /></MinusIconWrapper>
                      }
                      <FeatureName hasDesc={!!desc}>{text}</FeatureName>
                      {desc && (
                        <TooltipWrapper>
                          {desc}
                          {image && <ImageWrapper><Image src={image} alt='' height={150} width={250} layout='responsive' /></ImageWrapper>}
                        </TooltipWrapper>
                      )}
                    </FeatureText>
                  ))}
                </Category>
              ))}
            </div>
          </Column>
          <Column>
            <ColumnTitle>
              <Trans
                t={t}
                i18nKey='teams.TITLE'
                values={{
                  symbol: props.pricingInfo.symbol,
                  pricing: props.pricingInfo.group[plan]
                }}
                components={[
                  <SymbolPrice key={0} />,
                  <span key={1} />,
                  <LengthText key={2} />
                ]}
              />
            </ColumnTitle>
            <ColumnSubtitle>{t('teams.SUBTITLE')}</ColumnSubtitle>
            <ColumnDescription>{t('teams.DESCRIPTION')}</ColumnDescription>
            <ActionWrapper>
              <PrimaryAction {...PRIMARY_ACTION_PROPS} {...paymentsCtaAction} />
            </ActionWrapper>
            <div>
              {props.featuresInfo.map(({ id, text, features, group }) => (
                <Category key={id}>
                  <CategoryTitle>{text}</CategoryTitle>
                  {features.map(({ id, text, desc, image }) => (
                    <FeatureText key={id}>
                      {group
                        ? <CheckIconWrapper><Image src={CheckIcon} alt={t('teams.CHECK_ICON_ALT')} /></CheckIconWrapper>
                        : <MinusIconWrapper><Image src={MinusIcon} alt={t('teams.MINUS_ICON_ALT')} /></MinusIconWrapper>
                      }
                      <FeatureName hasDesc={!!desc}>{text}</FeatureName>
                      {desc && (
                        <TooltipWrapper>
                          {desc}
                          {image && <ImageWrapper><Image src={image} alt='' height={150} width={250} layout='responsive' /></ImageWrapper>}
                        </TooltipWrapper>
                      )}
                    </FeatureText>
                  ))}
                </Category>
              ))}
            </div>
          </Column>
        </Table>
      </Section>

      {/* FAQ */}
      <CenteredSection bgColor='gray'>
        <Title><Trans t={t} i18nKey='faq.TITLE'><Highlight color='green'/></Trans></Title>
        <FeatureGrid
          cols={2}
          descVisibleOnMobile
          list={[
            {
              title: t('faq.changePlan.QUESTION'),
              description: t('faq.changePlan.ANSWER')
            },
            {
              title: t('faq.freeVsPro.QUESTION'),
              description: t('faq.freeVsPro.ANSWER')
            },
            {
              title: t('faq.cancellation.QUESTION'),
              description: t('faq.cancellation.ANSWER')
            }
          ]}
        />
      </CenteredSection>

      {/* BottomCta */}
      <BottomCta action={ctaAction} {...t('BOTTOM_CTA')} />

      {/* Footer */}
      <Footer t={tCommon} />
    </>
  )
}

export default Pricing
